// @ts-check
// Prévia dos horários da página de agendamento (contrato 10 de agendamento, seção 14): os próximos dias com horário
// livre como a família vai ver, no fuso da página, calculados pelo servidor (ou pelo modo demonstração) com a mesma
// conta da página pública.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { listBookingSlots } from '../../data/booking-pages.js';
import { Button } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';
import { timeZoneNameInSentence } from '../../../../shared/time/time-zone-names.js';

const DAYS_SHOWN = 6;
const TIMES_PER_DAY = 8;

/**
 * @param {{ bookingPageId: string, timeZone: string, refreshKey: string }} props
 *   refreshKey: muda quando a página é salva, para a prévia refazer a conta
 */
export function BookingSlotPreview({ bookingPageId, timeZone, refreshKey }) {
  const [days, setDays] = useState(/** @type {import('../../data/booking-pages.js').BookingSlotDays['days'] | null} */ (null));
  const [hasError, setHasError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  useEffect(() => {
    let isCurrent = true;
    setDays(null);
    setHasError(false);
    listBookingSlots(bookingPageId)
      .then((result) => isCurrent && setDays(result?.days ?? []))
      .catch(() => {
        if (!isCurrent) return;
        setHasError(true);
        setDays([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [bookingPageId, refreshKey, reloadCount]);
  const dayFormat = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short' });
  const timeFormat = new Intl.DateTimeFormat('pt-BR', { timeZone, hour: '2-digit', minute: '2-digit' });
  const shown = (days ?? []).filter((day) => day.slots.length > 0).slice(0, DAYS_SHOWN);
  return html`<div class="booking-preview" data-testid="booking-slot-preview">
    <div class="booking-preview-header">
      <span class="input-hint">${t('bookingPages.preview.timeZone', { timeZone: timeZoneNameInSentence(timeZone) })}</span>
      <${Button} title=${t('bookingPages.preview.refresh')} icon="IconRefresh" variant="secondary" size="small" onClick=${() => setReloadCount((count) => count + 1)} />
    </div>
    ${days === null
      ? html`<${Skeleton} height=${120} radius=${8} />`
      : shown.length === 0
        ? html`<div class="booking-preview-empty"><${Icon} name="IconCalendarTime" size=${20} /><span>${hasError ? t('bookingPages.preview.error') : t('bookingPages.preview.empty')}</span></div>`
        : html`<ol class="booking-preview-days">
            ${shown.map((day) => html`<li key=${day.date} class="booking-preview-day">
              <span class="booking-preview-date">${dayFormat.format(new Date(`${day.date}T12:00:00Z`)).replace(/\./g, '')}</span>
              <span class="booking-preview-times">
                ${day.slots.slice(0, TIMES_PER_DAY).map((slot) => html`<span key=${slot.startsAt} class="booking-preview-time">${timeFormat.format(new Date(slot.startsAt))}</span>`)}
                ${day.slots.length > TIMES_PER_DAY ? html`<span class="booking-preview-more">${t('bookingPages.preview.more', { count: day.slots.length - TIMES_PER_DAY })}</span>` : null}
              </span>
            </li>`)}
          </ol>`}
  </div>`;
}
